import { useCallback, useMemo } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { saveOriginalLayout } from '@/api/trophies'
import type { LayoutItem, OriginalTrophy } from '@/api/trophies'
import { firstEmptySlot, layoutFromTrophies, resolveDrop } from '@/lib/shelf-layout'
import type { DragSource, DropTarget } from '@/lib/shelf-layout'

const QUERY_KEY = ['originalTrophies']

/**
 * 自由棚の配置。配置はクエリキャッシュから導出し、変更はキャッシュを楽観更新してから保存する。
 * 保存後（失敗時も）に再取得するので、失敗した変更はサーバーの値に戻る。
 */
export function useShelfLayout(trophies: OriginalTrophy[]) {
  const qc = useQueryClient()
  const layout = useMemo(() => layoutFromTrophies(trophies), [trophies])
  const knownIds = useMemo(() => new Set(trophies.map((t) => t.id)), [trophies])

  /**
   * 楽観更新して保存する。変更内容は、進行中の再取得を止めた後の最新キャッシュから compute で決める
   * （続けて呼ばれても、前の呼び出しの楽観更新を反映した状態から計算できる）。null なら何もしない。
   */
  const save = useCallback(async (compute: (current: OriginalTrophy[]) => LayoutItem[] | null) => {
    // 進行中の再取得が楽観更新を古い値で上書きしないよう止める
    await qc.cancelQueries({ queryKey: QUERY_KEY })
    const items = compute(qc.getQueryData<OriginalTrophy[]>(QUERY_KEY) ?? [])
    if (!items) return
    const posById = new Map(items.map((i) => [i.id, i.position]))
    qc.setQueryData<OriginalTrophy[]>(QUERY_KEY, (old) =>
      old?.map((t) => (posById.has(t.id) ? { ...t, position: posById.get(t.id) ?? null } : t)),
    )
    try {
      await saveOriginalLayout(items)
    } catch {
      toast.error('配置の保存に失敗しました')
    } finally {
      qc.invalidateQueries({ queryKey: QUERY_KEY })
    }
  }, [qc])

  const apply = useCallback((items: LayoutItem[]) => save(() => items), [save])

  /** 未配置のトロフィーを、空いている最初のスロットに置く（詳細の「棚に追加」） */
  const placeInFirstEmptySlot = useCallback((trophyId: number) => save((current) => {
    const target = current.find((t) => t.id === trophyId)
    if (!target || target.position !== null) return null
    return [{ id: trophyId, position: firstEmptySlot(layoutFromTrophies(current)) }]
  }), [save])

  const drop = useCallback((source: DragSource, target: DropTarget) => {
    const result = resolveDrop(layout, knownIds, source, target)
    if (result) void apply(result.items)
  }, [layout, knownIds, apply])

  return { layout, apply, drop, placeInFirstEmptySlot }
}
